<script lang="ts">
  import { ApiError } from '@services/api';
  import type { CommentType } from './types';
  import { commentService } from '@services/commentService';

  type Props = {
    comment: CommentType;
    loading: boolean;
    vote: (comment: CommentType) => void;
    onError: (error: Error) => void;
  };

  const { comment, vote, loading, onError }: Props = $props();

  const isUpvoted = $derived(comment.current_user_vote === 'upvote');
  const isDownvoted = $derived(comment.current_user_vote === 'downvote');

  async function handleVote(voteType: 'upvote' | 'downvote') {
    if (loading) return;
    try {
      const updatedComment = await commentService.vote(comment.id, voteType);
      vote(updatedComment);
    } catch (e: unknown) {
      if (e instanceof ApiError) {
        onError(e);
      } else {
        onError(e instanceof Error ? e : new Error(String(e)));
      }
    }
  }
</script>

<div class="vote-controls">
  <button
    class="vote-btn upvote"
    class:active={isUpvoted}
    onclick={() => handleVote('upvote')}
    disabled={loading}
    aria-label="Curtir"
    aria-pressed={isUpvoted}
  >
    <i class="fas fa-thumbs-up"></i>
  </button>
  <span class="score">{comment.score}</span>
  <button
    class="vote-btn downvote"
    class:active={isDownvoted}
    onclick={() => handleVote('downvote')}
    disabled={loading}
    aria-label="Descurtir"
    aria-pressed={isDownvoted}
  >
    <i class="fas fa-thumbs-down"></i>
  </button>
</div>

<style>
  .vote-controls {
    display: flex;
    align-items: center;
    color: #878a8c;
    margin-right: 12px;
  }

  .vote-btn {
    background: none;
    color: inherit;
    border: none;
    padding: 6px 8px;
    font-size: 14px;
    cursor: pointer;
    transition: color 0.1s ease-in-out;
    display: flex;
    align-items: center;
  }

  .vote-btn:hover:not(:disabled) {
    filter: brightness(90%);
  }

  .vote-btn.active {
    color: #0079d3;
  }

  .vote-btn.upvote.active {
    color: #0079d3;
  }

  .vote-btn.downvote.active {
    color: #ff4500;
  }

  .vote-btn:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }

  :global(.dark) .vote-btn.active {
    color: #f5c109;
  }

  :global(.dark) .vote-btn.upvote.active {
    color: #f5c109;
  }

  :global(.dark) .vote-btn.downvote.active {
    color: #f57242;
  }

  .score {
    margin: 0 4px;
    font-weight: 600;
    font-size: 14px;
    min-width: 18px;
    text-align: center;
  }

  @media (max-width: 600px) {
    .vote-btn {
      font-size: 13px;
      padding: 5px 6px;
    }

    .score {
      font-size: 13px;
    }
  }
</style>
